import type { Disposition, IpClassification, IpKind } from "./dmarc.js";

// Shared request/response DTOs between the Hono API and the React SPA.

export interface Me {
  sub: string;
  email: string;
  name?: string;
  picture?: string;
  // Backwards-compat: original SPA expected `roles: string[]` and used
  // `roles.includes("admin")` to gate Admin UI. We populate this with
  // the active-tenant role so existing checks keep working. Source of
  // truth for the actual model is `tenantRole` + `isPlatformAdmin`.
  roles: string[];
  // v3 — tenant context. `activeTenantId` is the tenant the API
  // resolved this request to (header X-Tenant-Id, with fallback to the
  // first tenant the user belongs to). `tenantRole` is null when the
  // user is acting as a platform admin in a tenant they aren't a
  // member of.
  activeTenantId: number;
  tenantRole: "owner" | "admin" | "member" | null;
  isPlatformAdmin: boolean;
}

export type Window = "24h" | "7d" | "30d" | "90d";

export interface OverviewResponse {
  domain: string | "all";
  window: Window;
  totals: {
    messages: number;
    dmarcPass: number;
    spfAligned: number;
    dkimAligned: number;
  };
  // Same metrics aggregated over the immediately-preceding window of
  // equal length. Lets the UI render deltas ("DMARC pass 92.3% ↑ from
  // 89.1%") without a separate API call.
  previousTotals: {
    messages: number;
    dmarcPass: number;
    spfAligned: number;
    dkimAligned: number;
  };
  trend: Array<{
    day: string; // yyyy-mm-dd
    messages: number;
    dmarcPass: number;
    spfAligned: number;
    dkimAligned: number;
  }>;
  // Operational health counters surfaced at the top of the dashboard.
  // Each maps to a deeplink the SPA renders in its HealthRow.
  health: {
    openAlerts: number;       // alert_events.acknowledged = 0
    criticalDomains: number;  // portfolio status = critical
    warningDomains: number;   // portfolio status = warning
    dnsChanges24h: number;    // dns_changes.detected_at in last 24h
    blocklistedIps: number;   // ip_reputation.listed = 1
  };
  // Top concerns across the whole monitored portfolio. Ranked client-
  // side by severity. Each item carries an `href` the SPA navigates to
  // when clicked.
  concerns: OverviewConcern[];
}

export interface OverviewConcern {
  severity: "critical" | "warning" | "info";
  kind:
    | "domain_critical"
    | "domain_warning"
    | "alert_recent"
    | "dns_change_recent"
    | "ip_blocklisted";
  domain?: string;
  summary: string;
  detail?: string;
  href: string;
  timestamp?: number;     // unix seconds — used to sort within severity
}

// F4 — Alert engine. Six rule kinds, each with its own threshold
// shape. The `threshold` field on AlertRule is the JSON parsed into
// the right shape per kind — discriminated union keeps it typed at
// the API edge.

export type AlertRuleKind =
  | "pass_rate_drop"
  | "dns_changed"
  | "no_reports_received"
  | "new_unclassified_source"
  | "volume_spike"
  | "legit_source_failing";

export interface PassRateDropThreshold {
  // Floor below which the rule fires. 0..1.
  threshold: number;
  // Lookback window in hours; rule compares the windowed pass rate
  // against the threshold. Default 24.
  hours: number;
  // Don't fire on tiny samples (e.g. only one record in 24h is noise).
  minMessages?: number;
}

export interface DnsChangedThreshold {
  // Lookback in hours. Defaults to "since last evaluation" (i.e. cron
  // interval) but operators can extend.
  lookbackHours: number;
  // Optional: only alert on these record types.
  recordTypes?: Array<"spf" | "dmarc" | "mx" | "dkim">;
}

export interface NoReportsReceivedThreshold {
  // Hours since last received report.
  thresholdHours: number;
}

export interface NewUnclassifiedSourceThreshold {
  // Volume floor in the lookback window — anything below this is noise.
  volumeThreshold: number;
  lookbackHours: number;
}

export interface VolumeSpikeThreshold {
  // Multiplier vs. the 7-day baseline.
  multiplier: number;
  lookbackHours: number;
  // Don't fire when the baseline is too small to be meaningful.
  minBaseline?: number;
}

export interface LegitSourceFailingThreshold {
  // Floor pass rate below which a previously-classified source counts
  // as "failing".
  threshold: number;
  lookbackHours: number;
  minMessages?: number;
}

export type AlertChannel =
  | { type: "webhook"; url: string };

export interface AlertRule {
  id: number;
  // v3 — owning tenant. Returned for read paths; ignored on create
  // (the server fills it from the authenticated user's tenant).
  tenantId: number;
  name: string;
  kind: AlertRuleKind;
  domain: string | null;          // NULL = applies to all monitored domains *in this tenant*
  threshold: Record<string, unknown>;
  channels: AlertChannel[];
  enabled: boolean;
  silentUntil: number | null;     // unix seconds
  createdAt: number;
}

export interface AlertEvent {
  id: number;
  ruleId: number;
  ruleName: string;
  ruleKind: AlertRuleKind;
  domain: string;
  firedAt: number;
  payload: Record<string, unknown>;
  summary: string;                // computed from payload at read time
  deliveredAt: number | null;
  deliveryError: string | null;
  acknowledged: boolean;
}

export interface CreateAlertRuleInput {
  name: string;
  kind: AlertRuleKind;
  domain?: string | null;
  threshold: Record<string, unknown>;
  channels: AlertChannel[];
  enabled?: boolean;
  silentUntil?: number | null;
}

export interface UpdateAlertRuleInput {
  name?: string;
  threshold?: Record<string, unknown>;
  channels?: AlertChannel[];
  enabled?: boolean;
  silentUntil?: number | null;
}

export interface AlertRunResponse {
  rulesEvaluated: number;
  candidatesFound: number;
  fired: number;             // events actually written (post-dedup)
  dedupSkipped: number;
  delivered: number;
  deliveryErrors: number;
}

export interface AlertTestResponse {
  rule: AlertRule;
  candidates: Array<{
    domain: string;
    payload: Record<string, unknown>;
    summary: string;
  }>;
}

// F3 — Portfolio aggregate view across every monitored organizational
// domain. One row per root, with subdomain-scoped reports rolled up
// under the root via the same expansion that the Domain detail page uses.

export type PortfolioStatus = "healthy" | "warning" | "stale" | "critical";

export interface PortfolioRow {
  domain: string;
  subdomainCount: number;
  publishedP?: "none" | "quarantine" | "reject";
  publishedSp?: "none" | "quarantine" | "reject";
  // 7-day window metrics (or whatever the caller passed).
  volume: number;
  dmarcPass: number;          // numerator only; rate is volume > 0 ? dmarcPass / volume : 0
  passRate: number;
  reportersCount: number;
  lastReportAt: number | null;
  hasRecentDnsChange: boolean;  // any DNS change recorded in last 48h
  status: PortfolioStatus;
  // Human-readable reasons for the current status. Empty for `healthy`.
  // Computed server-side so the UI doesn't have to duplicate the
  // threshold logic. Multiple reasons can apply (low pass rate AND
  // recent DNS change, for example).
  statusReasons: string[];
}

export interface PortfolioResponse {
  window: Window;
  rows: PortfolioRow[];
  totals: {
    domains: number;
    healthy: number;
    warning: number;
    stale: number;
    critical: number;
  };
}

export interface DomainSummary {
  domain: string;
  enabled: boolean;
  // The organizational (root) domain this row belongs to. For root
  // entries, `rootDomain === domain`. Null only for legacy rows that
  // predate migration 0006 — UI should treat null as "self" for safety.
  rootDomain: string | null;
  // For root entries: how many subdomains (rows where root_domain = self
  // but domain != self) have been observed. 0 for non-roots and for
  // roots that haven't seen subdomain reports yet.
  subdomainCount: number;
  lastReportAt: number | null; // unix seconds
  reportCount: number;
  issues: {
    absentFor?: number; // hours since last report, if >72
  };
}

// v5 — a domain first seen via an inbound report but NOT yet claimed
// (verified=0). Reports for it are held (not ingested) until the tenant
// proves ownership via the TXT challenge, at which point the held history
// is backfilled. Surfaced in the "Discovered domains" tray.
export interface DiscoveredDomain {
  domain: string;
  firstSeenAt: number | null; // unix seconds — earliest held report
  lastSeenAt: number | null; // unix seconds — most recent held report
  heldReportCount: number; // held payloads awaiting a claim
}

export interface TimeseriesPoint {
  day: string;
  messages: number;
  dmarcPass: number;
  spfAligned: number;
  dkimAligned: number;
}

export interface SourceRow {
  ip: string;
  ptr?: string;
  asn?: number;
  asnOrg?: string;
  country?: string;
  classification: IpClassification;
  // Inferred role of this IP for the queried domain. Today only
  // "forwarder" is detected (legitimate auto-forwarding via mailbox
  // providers like Google Workspace). Undefined when no special role is
  // detected — most IPs fall here.
  kind?: IpKind;
  // F8 — number of DNSBL providers that have this IP listed at the
  // last check. 0 = clean / not yet checked. Full provider breakdown
  // lives in IpDetail.reputation.
  blocklistedSources: number;
  messages: number;
  passRate: number; // 0..1 — DMARC pass (DKIM aligned OR SPF aligned)
  dkim: {
    passRate: number; // 0..1 — % of messages where DKIM aligned
    topSigner?: { domain: string; selector?: string };
  };
  spf: {
    passRate: number;          // 0..1 — % of messages where SPF aligned
    topEnvelope?: string;      // most common envelope-from / SPF domain
  };
  firstSeen: number;
  lastSeen: number;
}

export interface IpDetail {
  ip: string;
  ptr?: string;
  asn?: number;
  asnOrg?: string;
  country?: string;
  classification: IpClassification;
  // Inferred role across all domains the IP has appeared for. See
  // SourceRow.kind for semantics.
  kind?: IpKind;
  // F8 — every DNSBL the IP has been checked against. Empty array
  // means the sweep hasn't reached this IP yet (it's outside the
  // top-N volume cutoff).
  reputation: IpReputationCheck[];
  history: Array<{
    day: string;
    messages: number;
    dmarcPass: number;
    disposition: Disposition;
  }>;
  recentFailureSamples: IpFailureSample[];
}

export interface IpFailureSample {
  reportId: number;       // primary key in `reports`
  reportIdOriginal: string; // the provider-supplied id ("Google.com:5836858…")
  orgName: string;
  domain: string;
  day: string;            // yyyy-mm-dd of the report window start
  count: number;
  disposition: Disposition;
  dkimAligned: "pass" | "fail" | null;
  spfAligned: "pass" | "fail" | null;
  headerFrom?: string;
  envelopeFrom?: string;
  dkimResults: Array<{ domain: string; selector?: string; result: string }>;
  spfResults: Array<{ domain: string; scope?: string; result: string }>;
}

// Per-subdomain breakdown inside a root domain's window. Modern DMARC
// reports arrive scoped to the root (organizational) domain but each
// record can carry a different `header_from`, so this view is what
// turns "one report" into "what's each subdomain doing".
export interface SubdomainBreakdownRow {
  headerFrom: string;            // empty string when the report omitted it
  messages: number;
  passRate: number;              // 0..1 — DMARC pass (DKIM aligned OR SPF aligned)
  dkimPassRate: number;
  spfPassRate: number;
  topSourceIp?: string;          // IP with the most volume for this subdomain
  uniqueSourceIps: number;
  firstSeen: number;
  lastSeen: number;
}

export interface SubdomainBreakdownResponse {
  rootDomain: string;
  window: Window;
  rows: SubdomainBreakdownRow[];
}

export interface DomainSummaryResponse {
  domain: string;
  policy: PublishedPolicySnapshot | null;
  reporters: Array<{
    orgName: string;
    reportCount: number;
    lastSeenAt: number;
    firstSeenAt: number;
  }>;
  recentReports: Array<{
    id: number;
    reportId: string;
    orgName: string;
    dateStart: number;
    dateEnd: number;
    receivedAt: number;
    messageCount: number;
    passCount: number;
  }>;
}

// F6 — global search. The SPA fires this on every (debounced) query
// from the command palette. Results are grouped so the UI can render
// each kind with its own affordance + navigation target.

export interface SearchResultIp {
  ip: string;
  ptr?: string;
  classification?: IpClassification;
}

export interface SearchResultDomain {
  domain: string;
  rootDomain?: string;
}

export interface SearchResultHeaderFrom {
  headerFrom: string;
  rootDomain?: string;     // resolved organizational domain when known
  messageCount: number;    // 30d volume — UI uses it as a relevance hint
}

export interface SearchResponse {
  query: string;
  ips: SearchResultIp[];
  domains: SearchResultDomain[];
  headerFroms: SearchResultHeaderFrom[];
}

// F7 — DMARC progression wizard. Answers "can I advance the published
// DMARC policy to the next enforcement step?" by checking objective
// thresholds against the last 30 days of data.
//
// Output is rendered as a single card on /domains/:domain. Three
// visual states:
//   1. ready=true — every criterion passed; show DNS hint
//   2. ready=false, dataReady=true — show failing criteria + reasons
//   3. dataReady=false — "still collecting; N days remaining"

export interface ProgressionCriterion {
  id:
    | "dkim_aligned"
    | "dmarc_pass"
    | "unknown_classification_volume"
    | "new_ips_per_day";
  label: string;
  // Numeric current value with unit-aware rendering on the client.
  current: number;
  threshold: number;
  unit: "rate" | "percent" | "count_per_day";
  // Lower-is-better (unknown volume, new IPs) vs higher-is-better
  // (DKIM/DMARC alignment) — drives whether `current >= threshold` or
  // `current <= threshold` is the pass condition.
  comparator: "gte" | "lte";
  passed: boolean;
  description: string;
}

export interface ProgressionWizardResponse {
  domain: string;
  currentPolicy: "none" | "quarantine" | "reject" | null;
  targetPolicy: "quarantine" | "reject" | null;  // null when fully enforced or no data
  // Whole days since the first report for this domain (or any
  // subdomain). Drives the "still collecting" state.
  dataAgeDays: number;
  dataReady: boolean;          // dataAgeDays >= 14
  ready: boolean;              // dataReady && every criterion passed
  criteria: ProgressionCriterion[];
  // One-line summary the card uses as a heading.
  recommendation: string;
  // Suggested DNS edit when ready=true. Just the relevant tag swap,
  // not the full record — operators usually have their own SPF/rua
  // configured and shouldn't paste a generic example over it.
  dnsHint?: string;
}

export interface PublishedPolicySnapshot {
  p?: "none" | "quarantine" | "reject";
  sp?: "none" | "quarantine" | "reject";
  pct?: number;
  adkim?: "r" | "s";
  aspf?: "r" | "s";
  observedAt: number;     // unix seconds — when this policy was last observed
}

export interface ReportSummary {
  id: number;
  reportId: string;
  orgName: string;
  domain: string;
  dateStart: number;
  dateEnd: number;
  receivedAt: number;
  recordCount: number;
  rawUrl: string; // signed R2 URL
}

export interface QuarantineRow {
  id: number;
  r2Key: string;
  reason: string;
  receivedAt: number;
  errorDetail?: string;
  // Captured at quarantine time when available — null on legacy rows
  // (pre-migration 0005) or when the upstream signal didn't carry it
  // (e.g. r2_object_missing has no subject/size).
  senderFrom?: string;
  subject?: string;
  filename?: string;
  sizeBytes?: number;
}

// Outcome of re-running quarantined payloads back through the ingest
// pipeline (e.g. after a parser fix). Each row that now ingests/dedups is
// removed from quarantine; rows that still fail are re-quarantined with the
// latest reason. `stillFailing` + `missing` + `errors` stay in quarantine.
export interface QuarantineReprocessResult {
  scanned: number;
  ingested: number;
  duplicate: number;
  stillFailing: number;
  missing: number; // R2 payload gone — nothing to reprocess
  errors: Array<{ id: number; message: string }>;
}

// v3 — Per-tenant roles. 'owner' has the strictest powers (currently
// just "can delete the tenant"; later "manages billing"); 'admin' can
// do anything except those owner-only actions; 'member' is read-only.
export type TenantRole = "owner" | "admin" | "member";

// Kept as an alias for SPA/API backwards compatibility while older
// callers migrate. Same string union as TenantRole.
export type UserRole = TenantRole;

export interface TenantUser {
  email: string;
  role: TenantRole;
  addedAt: number;
}

// v3 / R1 — Self-service tenant DTOs.
export interface TenantSummary {
  id: number;
  slug: string;
  displayName: string;
  // Local part of the inbound mailbox. Surfaced so the onboarding
  // wizard can show the customer the exact `rua=mailto:reports-{x}@…`
  // they need to publish.
  mailboxLocal: string;
  // Fully composed inbound address — `reports-{mailboxLocal}@{host}` —
  // where the host comes from the worker's `INBOUND_MAILBOX_DOMAIN`.
  // Until the host is configured the placeholder
  // `<dmarc-mailbox-host>` ships back so the SPA can still render the
  // structure without hardcoding it client-side.
  mailboxAddress: string;
  status: "active" | "suspended" | "deleted";
  plan: string;
  maxDomains: number;
  maxSeats: number;
  // Soft monthly cap on DMARC-reported message volume (SUM of
  // daily_aggregates.messages across the tenant's domains in the current
  // calendar month, UTC). "Soft" — exceeding it never drops reports
  // (PRD §6, no silent loss); it only surfaces an over-quota badge in the
  // usage view and (future) fires an operator alert. Etus (tenant 1) uses
  // the 999999999 "no quota" sentinel.
  maxMessagesMonth: number;
  retentionDays: number;
  createdAt: number;
}

export interface CreateTenantInput {
  // Customer-facing display label. The slug is derived from this
  // server-side (lowercased, alphanumeric, deduplicated against
  // existing tenants).
  displayName: string;
  // v4 self-service signup: legal acceptance + anti-abuse. The owner must
  // tick "I accept the Terms & Privacy" (the backend rejects acceptTos !==
  // true and records tos_accepted_at/tos_version). `turnstileToken` is the
  // Cloudflare Turnstile token the backend verifies server-side. Both are
  // optional in the type for non-signup callers, but the POST /api/tenants
  // handler requires them for public self-service.
  acceptTos?: boolean;
  tosVersion?: string;
  turnstileToken?: string;
}

// Current Terms/Privacy version the SPA presents and the backend stamps on
// acceptance. Bump when the legal text changes so consent is auditable.
export const TOS_VERSION = "2026-06-06";

export interface UpdateTenantInput {
  displayName?: string;
}

// Per-tenant entry in `GET /api/tenants` (workspaces I belong to). The
// SPA's OrgSwitcher renders this list; `role` carries the user's
// membership role inside each tenant. Platform admins see every active
// tenant with `role: null` when they aren't a direct member.
export interface MyTenant extends TenantSummary {
  role: TenantRole | null;
}

// Body of `PATCH /api/platform/tenants/:id`. All fields optional —
// platform admin updates the columns they care about and leaves the
// rest. `status` lets Etus suspend/reactivate a tenant; mailbox/slug
// are immutable through this endpoint (the customer's DMARC publish
// depends on the mailbox staying stable).
export interface UpdatePlatformTenantInput {
  displayName?: string;
  plan?: string;
  maxDomains?: number;
  maxSeats?: number;
  // Soft monthly message-volume cap. Like retention, changing `plan`
  // moves this to the plan default unless this field is set explicitly in
  // the same request (an override wins).
  maxMessagesMonth?: number;
  retentionDays?: number;
  status?: "active" | "suspended" | "deleted";
}

// Body of `POST /api/platform/tenants`. The operator provisions a
// workspace on a customer's behalf. Only `displayName` is required; the
// slug + inbound mailbox are generated server-side (same helpers as
// self-service signup). Quotas/retention default from the chosen plan
// unless set explicitly. `ownerEmail`, when present, seeds the first
// `owner` membership so the workspace isn't created member-less.
export interface CreatePlatformTenantInput {
  displayName: string;
  plan?: string;
  maxDomains?: number;
  maxSeats?: number;
  maxMessagesMonth?: number;
  retentionDays?: number;
  ownerEmail?: string;
}

// Row in the platform admin console. Extends the summary with
// operational counters the operator needs to triage workspaces.
export interface PlatformTenantRow extends TenantSummary {
  memberCount: number;
  domainCount: number;
  lastReportAt: number | null;
  deletedAt: number | null;
}

// v5 — per-tenant usage / consumption view (`GET /api/tenants/usage`).
//
// The three control metrics are of two kinds. `domains` and `seats` are
// *state* (a count that exists at a point in time), so their day-by-day
// history is reconstructed from a nightly snapshot in `tenant_usage_daily`.
// `messages` is a *flow* (DMARC volume reported that day), summed from
// `daily_aggregates`. The current day is always synthesized live so the
// view is never a full day stale.

// One day in the current calendar month.
export interface TenantUsageDay {
  day: string;        // yyyy-mm-dd (UTC)
  messages: number;   // DMARC volume reported that day
  domains: number;    // committed root domains snapshot (state)
  seats: number;      // tenant_users snapshot (state)
  // True for today's row — synthesized live, not yet persisted by cron.
  live?: boolean;
}

// A closed prior month, aggregated. `messages` sums the flow; `domains`
// and `seats` report the peak (MAX) that month — the figure that mattered
// for capacity, not an average that would understate a mid-month spike.
export interface TenantUsageMonth {
  month: string;         // yyyy-mm (UTC)
  messages: number;      // SUM over the month
  domainsPeak: number;   // MAX daily snapshot
  seatsPeak: number;     // MAX daily snapshot
}

export interface TenantUsageResponse {
  limits: {
    maxDomains: number;
    maxSeats: number;
    maxMessagesMonth: number;
  };
  // Live figures for "right now" — what the SPA shows in the quota cards.
  current: {
    domains: number;
    seats: number;
    messagesThisMonth: number;
  };
  daily: TenantUsageDay[];     // current month, ascending by day
  monthly: TenantUsageMonth[]; // prior months, descending (most recent first)
}

export interface DomainVerificationChallenge {
  name: string;   // `_etus-dmarc-verify.{domain}`
  type: "TXT";
  value: string;  // `etus-verify={token}`
}

export interface AddDomainResponse {
  domain: string;
  verified: boolean;
  challenge: DomainVerificationChallenge | null;
  // Pre-built DMARC record the customer should publish at
  // `_dmarc.{domain}` once the verification is done. Includes the
  // tenant's `mailbox_local` so they don't have to assemble it.
  dmarcRecord: {
    name: string;
    type: "TXT";
    value: string;
  };
}

export interface VerifyDomainOutcome {
  verified: boolean;
  // The TXT values we actually observed at the challenge name, so the
  // SPA can show "you published `etus-verify=wrong-token`, we expected
  // `etus-verify={right-token}`" when the match fails.
  observed: string[];
  error?: string;
}

export interface QuotaInfo {
  current: number;
  limit: number;
  plan: string;
}

// Pre-v3 name. Same shape as TenantUser; aliased so older imports keep
// working until the SPA fully renames its hooks. Schedule for removal
// once the SPA stops importing it.
export type MonitoredUser = TenantUser;

export interface CreateTenantUserInput {
  email: string;
  role: TenantRole;
}

export interface UpdateTenantUserInput {
  role: TenantRole;
}

export type CreateMonitoredUserInput = CreateTenantUserInput;
export type UpdateMonitoredUserInput = UpdateTenantUserInput;

// v2 / F2 — DNS configuration drift.
//
// `current` lists the latest snapshot per record_type (one row each for
// 'spf' / 'dmarc' / 'mx' / 'dkim:<selector>'). `changes` is the most
// recent diff timeline, newest first.

export interface DnsSnapshotEntry {
  recordType: string;        // 'spf' | 'dmarc' | 'mx' | 'dkim:<selector>'
  rawValue: string | null;
  capturedAt: number;
}

export interface DnsChangeEntry {
  recordType: string;
  beforeRaw: string | null;
  afterRaw: string | null;
  diffSummary: string;
  detectedAt: number;
}

export interface DnsHistoryResponse {
  domain: string;
  current: DnsSnapshotEntry[];
  changes: DnsChangeEntry[];
}

export interface DnsDriftRunResponse {
  domain: string | "all";
  domainsScanned: number;
  snapshotsInserted: number;
  changesDetected: number;
  errors: Array<{ domain: string; message: string }>;
}

// One-shot quarantine metadata backfill (POST /api/admin/quarantine/backfill).
export interface QuarantineBackfillResponse {
  scanned: number;
  recovered: number;          // rows where at least one column was filled
  fullyRecovered: number;     // rows where every previously-NULL column got a value
  unrecoverable: number;      // R2 object gone / no signal to recover from
  errors: Array<{ id: number; message: string }>;
}

// One-shot backfill of root_domain on legacy monitored_domains rows
// (POST /api/admin/monitored-domains/backfill-roots).
export interface MonitoredDomainRootsBackfillResponse {
  scanned: number;            // rows with root_domain IS NULL at the start
  updated: number;            // rows that received a root_domain
  rootsCreated: number;       // organizational domain rows auto-inserted
}

// F8 — IP reputation. The dashboard surfaces a per-IP record listing
// every DNSBL provider it was checked against; `listed` flags whether
// the provider returned a positive (blocklisted) answer at the last
// check. `delistingUrl` is provided by the worker so the SPA can link
// users straight to the right delisting form.
export interface IpReputationCheck {
  source: string;             // 'spamhaus_zen' | …
  sourceLabel: string;        // 'Spamhaus zen'
  listed: boolean;
  returnCode?: string;        // 127.0.0.x when listed
  category?: string;          // human-readable interpretation of the return code
  checkedAt: number;
  delistingUrl?: string;      // pre-filled delisting URL for the IP
}

export interface ReputationSweepResponse {
  scanned: number;            // distinct IPs queried
  listed: number;             // checks that returned listed=true
  errors: number;             // network / parse failures (not stored)
  providers: number;          // DNSBL providers consulted
}

// Status of the reputation feature. Empty `providers` means no DNSBL
// is currently usable — typically because no SPAMHAUS_DQS_KEY is
// configured. The admin UI surfaces this as a clear warning instead
// of silently producing no results.
export interface ReputationStatusResponse {
  providers: Array<{ id: string; label: string }>;
}

// Cleanup of bogus listings that the pre-safeguard sweep stored when
// Spamhaus rejected queries from public DoH resolvers
// (POST /api/admin/reputation/cleanup).
export interface ReputationCleanupResponse {
  deleted: number;
}

// Portfolio view for /dns-monitor.
export interface DnsMonitorDomainRow {
  domain: string;
  enabled: boolean;
  lastCapturedAt: number | null;     // null = never snapshotted
  hasSpf: boolean;
  hasDmarc: boolean;
  dmarcP: string | null;             // 'none' | 'quarantine' | 'reject' | null
  hasMx: boolean;
  mxCount: number;                   // 0 when absent
  dkimSelectorCount: number;
  lastChangeAt: number | null;       // null = no changes recorded yet
}

export interface DnsMonitorChangeRow {
  domain: string;
  recordType: string;                // 'spf' | 'dmarc' | 'dkim:<selector>'
  beforeRaw: string | null;
  afterRaw: string | null;
  diffSummary: string;
  detectedAt: number;
}

export interface DnsMonitorOverviewResponse {
  domains: DnsMonitorDomainRow[];
  changes: DnsMonitorChangeRow[];
}

// F10 — TLS-RPT ingest outcome. Same three terminal states as the
// DMARC upload path; reused by the queue handler and (when wired) a
// future manual upload endpoint.
export type TlsRptUploadOutcome =
  | { status: "ingested"; reportRowId: number; r2Key: string; policyDomain: string }
  | { status: "duplicate"; r2Key: string }
  | { status: "quarantined"; reason: string; detail?: string; r2Key: string };

// Per-domain TLS-RPT summary card (GET /api/domains/:domain/tls-rpt).
// Empty (no reports yet) renders as "no data" in the UI; otherwise
// shows the last report window, totals and top failure types.
export interface TlsRptSummaryResponse {
  domain: string;
  hasData: boolean;
  // Latest report observed for this domain (or any subdomain rolled
  // up under it).
  latestReport?: {
    orgName: string;
    reportId: string;
    dateStart: number;
    dateEnd: number;
    receivedAt: number;
  };
  // Totals over the last 30 days.
  totals: {
    success: number;
    failure: number;
  };
  // Failure breakdown over the last 30 days, top N by count.
  failureBreakdown: Array<{
    resultType: string;
    failedSessionCount: number;
  }>;
}

// Result of POST /api/reports/upload. Three terminal states:
//   - ingested: first time we see this report; a new `reports` row exists.
//   - duplicate: the report_id + org + date_range was already ingested.
//   - quarantined: the file parsed or validated as unusable; preserved in R2.
export type UploadOutcome =
  | { status: "ingested"; reportRowId: number; newIps: string[]; r2Key: string }
  | { status: "duplicate"; r2Key: string }
  | { status: "quarantined"; reason: string; detail?: string; r2Key: string };
